// MessageSidebar.jsx
import React from 'react'
import {
    List,
    ListItemButton,
    ListItemText,
    Typography,
    Divider,
    Box,
    AvatarGroup,
    Avatar,
    Paper,
} from '@mui/material'
import ProfileIcon from './ProfileIcon'

function MessageSidebar({ onSelectConversation, selectedConversation, events }) {
    if (!events || events.length === 0) {
        return (
            <Box sx={{ p: 2, color: 'text.secondary' }}>
                No conversations found.
            </Box>
        )
    }

    return (
        <List sx={{ overflowY: 'auto', height: '100%', p: 0, bgcolor: '#f7f8fa' }}>
            {events.map((event) => (
                <React.Fragment key={event.eventId}>
                    {event.conversations?.map((conversation) => {
                        const isSelected = selectedConversation?.id === conversation.id
                        const lastMessageTime = new Date(conversation.timestamp).toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                        })
                        // Simulate online for all for now
                        const isOnline = true
                        return (
                            <ListItemButton
                                key={conversation.id}
                                onClick={() => onSelectConversation(conversation)}
                                selected={isSelected}
                                sx={{
                                    py: 1.5,
                                    px: 2.5,
                                    borderRadius: 2,
                                    mb: 1,
                                    alignItems: 'center',
                                    borderLeft: isSelected ? '4px solid #4F46E5' : '4px solid transparent',
                                    background: isSelected ? 'rgba(79,70,229,0.06)' : 'transparent',
                                    boxShadow: isSelected ? '0 2px 8px 0 rgba(79,70,229,0.04)' : 'none',
                                    '&.Mui-selected': {
                                        background: 'rgba(79,70,229,0.10)',
                                        '&:hover': {
                                            background: 'rgba(79,70,229,0.13)',
                                        },
                                    },
                                    '&:hover': {
                                        background: 'rgba(0, 0, 0, 0.03)',
                                    },
                                    transition: 'background-color 0.2s ease',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    {/* Avatar/Icon with green dot */}
                                    <Box sx={{ position: 'relative', minWidth: 48, height: 48 }}>
                                        <Box sx={{
                                            borderRadius: '12px',
                                            overflow: 'hidden',
                                            background: conversation.participants?.length > 2 
                                                ? 'linear-gradient(45deg, #4F46E5, #8a63f2)'
                                                : '#f0f0f0',
                                            width: 48,
                                            height: 48,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                            {conversation.participants?.length > 2 ? (
                                                <AvatarGroup
                                                    max={3}
                                                    sx={{
                                                        '& .MuiAvatar-root': {
                                                            width: 24,
                                                            height: 24,
                                                            fontSize: '0.75rem',
                                                            border: '2px solid white',
                                                        },
                                                    }}
                                                >
                                                    {conversation.participants.map((participant, idx) => (
                                                        <Avatar
                                                            key={idx}
                                                            src={participant.profilePicture}
                                                            alt={participant.name}
                                                            sx={{ bgcolor: '#4F46E5' }}
                                                        >
                                                            {participant.name?.charAt(0)}
                                                        </Avatar>
                                                    ))}
                                                </AvatarGroup>
                                            ) : (
                                                <ProfileIcon
                                                    user={conversation.participants?.find(p => p.userId !== conversation.senderId)}
                                                    size={32}
                                                />
                                            )}
                                        </Box>
                                        {/* Green dot for online */}
                                        {isOnline && (
                                            <Box sx={{
                                                position: 'absolute',
                                                bottom: 4,
                                                right: 4,
                                                width: 10,
                                                height: 10,
                                                bgcolor: '#22c55e',
                                                border: '2px solid #fff',
                                                borderRadius: '50%',
                                            }} />
                                        )}
                                    </Box>
                                    {/* Content */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: isSelected ? '#4F46E5' : '#232946',
                                                    fontSize: '1rem',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {conversation.name}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'text.secondary',
                                                    fontSize: '0.8rem',
                                                    ml: 1,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {lastMessageTime}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                fontSize: '0.92rem',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {conversation.lastMessage}
                                        </Typography>
                                    </Box>
                                </Box>
                            </ListItemButton>
                        )
                    })}
                </React.Fragment>
            ))}
        </List>
    )
}

export default MessageSidebar
